<script setup lang="ts">
/**
 * 一次性註冊碼＋安裝指令。**明碼只在建立／重發的那個 API 回應裡出現一次**——
 * 關掉這個畫面就再也拿不到（只能重發，而重發會讓舊 agent 金鑰立即失效），
 * 所以文案要把這件事講死，並提供複製鈕。
 */
import { computed, ref, watch } from 'vue'

import type { EnrollInfo, EnrollInstaller } from '@/api/hosting'

const props = defineProps<{ enroll: EnrollInfo; hostName?: string }>()

const copied = ref<'code' | 'command' | null>(null)
const copyFailed = ref(false)

/**
 * 批141（裁示 13）：註冊碼**不綁種類**——同一張碼可以裝任何一種已登錄的 agent，後端在
 * `installers` 裡每一種給一條安裝指令。只有一種（或舊後端沒有這一欄）就不給選。
 * 預設選中的是與 `command` 相同的那一條（後端的預設種類），找不到才取第一條。
 */
const installers = computed<EnrollInstaller[]>(() => props.enroll.installers ?? [])
const kind = ref('')
watch(
  () => props.enroll,
  (e) => {
    const list = e.installers ?? []
    kind.value = (list.find((i) => i.command === e.command) ?? list[0])?.kind ?? ''
  },
  { immediate: true },
)
const selected = computed<EnrollInstaller | null>(
  () => installers.value.find((i) => i.kind === kind.value) ?? null,
)
// 一種 agent 都沒登錄時後端給 `command: null`（批146 起不再自己組舊的 `/agent.sh`）→ 空字串＝不顯示指令。
const command = computed(() => selected.value?.command ?? props.enroll.command ?? '')

async function copy(kind: 'code' | 'command') {
  const text = kind === 'code' ? props.enroll.code : command.value
  try {
    await navigator.clipboard.writeText(text)
    copyFailed.value = false
    copied.value = kind
    window.setTimeout(() => {
      if (copied.value === kind) copied.value = null
    }, 2000)
  } catch {
    // 非 HTTPS／瀏覽器不給剪貼簿權限時走這裡：請對方自己選取複製。
    copyFailed.value = true
  }
}
</script>

<template>
  <div class="enroll">
    <p class="warn">
      ⚠️ 這串註冊碼<strong>只會出現這一次</strong>{{ hostName ? `（主機 ${hostName}）` : '' }}——
      關掉本視窗後無法再查看，只能重發新碼（重發會讓已註冊的 agent 立即失效）。
      有效期 {{ enroll.expires_in_minutes }} 分鐘，且<strong>只能用一次</strong>。
    </p>

    <label v-if="installers.length > 1" class="field">
      <span class="field-label">代管種類</span>
      <select v-model="kind">
        <option v-for="i in installers" :key="i.kind" :value="i.kind">{{ i.label }}（{{ i.kind }}）</option>
      </select>
      <small class="subtle">同一張註冊碼可以裝任何一種已登錄的 agent；依這台要跑的 agent 選對應的安裝指令。</small>
    </label>

    <div class="field">
      <div class="field-label">
        在該台 VM 上以 root 執行{{ selected && installers.length > 1 ? `（${selected.label}）` : '' }}：
      </div>
      <pre v-if="command" class="cmd">{{ command }}</pre>
      <p v-else class="subtle">尚未登錄任何代管種類，產生不了安裝指令——請先到〈代管種類〉登錄。</p>
      <div class="acts">
        <button v-if="command" type="button" class="btn sm primary" @click="copy('command')">
          {{ copied === 'command' ? '已複製 ✓' : '複製安裝指令' }}
        </button>
        <button type="button" class="btn sm" @click="copy('code')">
          {{ copied === 'code' ? '已複製 ✓' : '只複製註冊碼' }}
        </button>
      </div>
    </div>

    <div class="field">
      <div class="field-label">註冊碼</div>
      <code class="code">{{ enroll.code }}</code>
    </div>

    <p v-if="copyFailed" class="subtle copyfail">
      瀏覽器不允許自動複製（需 HTTPS 或已授權剪貼簿）——請手動選取上方文字複製。
    </p>
  </div>
</template>

<style scoped>
.enroll {
  display: grid;
  gap: var(--sp-3);
}
.warn {
  margin: 0;
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.cmd {
  margin: 0;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-mono);
  font-size: var(--fs-11);
  white-space: pre-wrap;
  word-break: break-all;
  user-select: all;
}
.code {
  display: block;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  font-family: var(--font-mono);
  font-size: var(--fs-13);
  letter-spacing: 0.04em;
  word-break: break-all;
  user-select: all;
}
.acts {
  display: flex;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
}
.copyfail {
  color: var(--danger);
}
</style>
